import { isRouteActive, Sidebar, SidebarItem, SidebarLink } from 'features';
import { useTranslation } from 'i18n';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Divider, List } from 'ui';
import { HomeOutlined } from 'ui/icons';

import { getTabs } from '../../../ground-control/data';

const hasActiveLink = (tab: ReturnType<typeof getTabs>[0], currentPath: string) => {
  return tab.links.some((link) => isRouteActive(currentPath, link.href));
};

const calculateExpandedState = (tabs: ReturnType<typeof getTabs>, currentPath: string): Record<string, boolean> => {
  const initialState: Record<string, boolean> = {};

  const firstActiveTab = tabs.find((tab) => hasActiveLink(tab, currentPath));

  tabs.forEach((tab) => {
    initialState[tab.label] = firstActiveTab ? tab.label === firstActiveTab.label : false;
  });

  return initialState;
};

const tabs = getTabs();
const mainSidebarItems = tabs.filter((tab) => tab.label !== 'Support');
const supportTab = tabs.find((tab) => tab.label === 'Support');

export const InternalSidebar = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation('orders');
  const { pathname } = useLocation();

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    calculateExpandedState(mainSidebarItems, pathname)
  );

  useEffect(() => {
    const activeList = calculateExpandedState(mainSidebarItems, pathname);

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setExpandedItems((prev) => ({
      ...prev,
      ...activeList,
    }));
  }, [pathname]);

  return (
    <Sidebar onClose={onClose} isOpen={isOpen}>
      <div>
        <SidebarLink
          label={t('internalHeader.links.groundControl')}
          href="/ground-control/"
          icon={<HomeOutlined />}
          dense={false}
          indent={false}
          isLegacy={false}
        />
      </div>
      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {mainSidebarItems.map((tab) => (
          <SidebarItem
            key={tab.label}
            label={tab.label}
            icon={tab.icon}
            links={tab.links}
            isExpanded={expandedItems[tab.label] || false}
            onToggleExpand={() => {
              setExpandedItems((prev) => ({
                ...prev,
                [tab.label]: !prev[tab.label],
              }));
            }}
          />
        ))}
      </List>
      <Box sx={{ justifySelf: 'center' }}>
        <Divider />
        {supportTab?.links.map((link) => (
          <SidebarLink
            key={link.label}
            label={link.label}
            href={link.href}
            icon={link.icon}
            dense={false}
            indent={false}
            isLegacy={link.isLegacy}
          />
        ))}
      </Box>
    </Sidebar>
  );
};
